import { useThree, useFrame, useLoader } from "@react-three/fiber";
import { useMemo, useRef, useEffect } from "react";
import * as THREE from "three";
import gsap from "gsap";
import { GRID_CONFIG } from "../../config/grid";
import { calculateObliqueMatrix } from "../../utils/projection";
import type { ProjectionParams } from "../../config/types";

const vertexShader = `
varying vec2 vUv;
void main() {
  vUv = uv;
  // Force full-screen quad at far plane (z=0.999 in NDC)
  gl_Position = vec4(position.xy, 0.999, 1.0);
}
`;

const fragmentShader = `
precision highp float;

uniform mat4 uInverseViewProj;
uniform mat4 uInverseOblique;
uniform float uGridSize;
uniform float uTime;
uniform float uIsDarkMode; // 0.0 = Light, 1.0 = Dark
uniform sampler2D uPaperTexture; // 종이 텍스처 (배경용)
uniform float uTextureScale; // 텍스처 스케일

varying vec2 vUv;

// 그리드 라인 그리기
float gridLine(vec2 pos, float lineWidth) {
  vec2 grid = abs(fract(pos - 0.5) - 0.5) / fwidth(pos);
  float line = min(grid.x, grid.y);
  return 1.0 - min(line / lineWidth, 1.0);
}



void main() {
  // --- 좌표 계산 ---
  vec2 ndc = vUv * 2.0 - 1.0;
  vec4 ndcNear = vec4(ndc, -1.0, 1.0);
  vec4 ndcFar = vec4(ndc, 1.0, 1.0);
  vec4 worldNear = uInverseViewProj * ndcNear;
  vec4 worldFar = uInverseViewProj * ndcFar;
  worldNear /= worldNear.w;
  worldFar /= worldFar.w;
  vec3 rayOrigin = worldNear.xyz;
  vec3 rayDir = normalize(worldFar.xyz - worldNear.xyz);
  vec4 realOrigin4 = uInverseOblique * vec4(rayOrigin, 1.0);
  vec3 realOrigin = realOrigin4.xyz / realOrigin4.w;
  vec4 realDir4 = uInverseOblique * vec4(rayDir, 0.0);
  vec3 realDir = normalize(realDir4.xyz);
  float t = -realOrigin.y / realDir.y;
  if (t < 0.0) discard;
  vec3 intersectPoint = realOrigin + realDir * t;
  vec2 worldPos = intersectPoint.xz;

  // --- 1. 배경 설정 ---
  vec3 lightBase = vec3(0.96, 0.96, 0.96);
  vec3 darkBase = vec3(0.02, 0.02, 0.02);
  vec3 baseColor = mix(lightBase, darkBase, uIsDarkMode);
  
  // 배경에 종이 텍스처 적용 (라이트/다크 모드 각각 다르게)
  vec2 paperTexCoord = worldPos * uTextureScale * 0.4;
  vec3 paperColor = texture2D(uPaperTexture, paperTexCoord).rgb;
  // 텍스처 밝기 값
  float paperBrightness = dot(paperColor, vec3(0.299, 0.587, 0.114));
  
  vec3 texturedBase;
  if (uIsDarkMode > 0.5) {
    // 다크 모드: 텍스처로 약간 밝게
    texturedBase = baseColor + (paperBrightness - 0.5) * 0.15;
  } else {
    // 라이트 모드: 텍스처로 약간 어둡게
    texturedBase = baseColor - (1.0 - paperBrightness) * 0.1;
  }
  texturedBase = clamp(texturedBase, vec3(0.0), vec3(1.0));
  
  vec3 finalGradient = texturedBase;
  
  // --- 2. 그리드 렌더링 ---
  float grid = 0.0;
  vec3 gridColor = vec3(0.0);

  vec2 gridUv = worldPos / uGridSize; 
  float gridIntensity = gridLine(gridUv, 1.0);
  
  // 그리드 색상 (다크/라이트 모드에 따라)
  vec3 baseGridColor;
  if (uIsDarkMode > 0.5) {
    baseGridColor = vec3(0.35); // 다크 모드
  } else {
    baseGridColor = vec3(0.65); // 라이트 모드
  }
  gridColor = clamp(baseGridColor, vec3(0.0), vec3(1.0));
  
  // 그리드 강도 (라이트 모드에서 더 강하게)
  float gridStrength = uIsDarkMode > 0.5 ? 0.25 : 0.4;
  grid = gridIntensity * gridStrength;

  vec3 finalColor = finalGradient + gridColor * grid;
  
  // --- 3. 도로 렌더링 ---
  // 도로는 이제 별도의 3D 메시로 렌더링되므로 shader에서 제거
  // 이렇게 하면 GPU가 자동으로 frustum culling과 LOD를 처리
  
  gl_FragColor = vec4(finalColor, 1.0);
}
`;

interface InfiniteBackgroundProps {
  projectionParams: ProjectionParams;
  getPanOffset: () => THREE.Vector3;
  isDarkMode?: boolean;
}

/**
 * 무한 배경 + 그리드 렌더링 컴포넌트
 * Full-Screen Quad (FSQ) GLSL 셰이더 방식으로 최적화
 */
export function InfiniteBackground({
  projectionParams,
  getPanOffset,
  isDarkMode = true,
}: InfiniteBackgroundProps) {
  const { camera, clock } = useThree();
  const materialRef = useRef<THREE.ShaderMaterial>(null);

  // 종이 텍스처 로드
  const paperTexture = useLoader(
    THREE.TextureLoader,
    import.meta.env.BASE_URL + "/textures/paper1.jpg"
  );

  // 텍스처 설정 (타일링)
  useMemo(() => {
    paperTexture.wrapS = THREE.RepeatWrapping;
    paperTexture.wrapT = THREE.RepeatWrapping;
    paperTexture.minFilter = THREE.LinearMipmapLinearFilter;
    paperTexture.magFilter = THREE.LinearFilter;
  }, [paperTexture]);

  const uniforms = useMemo(
    () => ({
      uInverseViewProj: { value: new THREE.Matrix4() },
      uInverseOblique: { value: new THREE.Matrix4() },
      uGridSize: { value: GRID_CONFIG.CELL_SIZE },
      uTime: { value: 0 },
      uIsDarkMode: { value: isDarkMode ? 1.0 : 0.0 },
      uPaperTexture: { value: paperTexture },
      uTextureScale: { value: 1.0 },
    }),
    [paperTexture]
  );

  useEffect(() => {
    if (materialRef.current) {
      // Smooth transition for isDarkMode
      gsap.to(materialRef.current.uniforms.uIsDarkMode, {
        value: isDarkMode ? 1.0 : 0.0,
        duration: 0.5,
        ease: "power2.inOut",
      });
    }
  }, [isDarkMode]);

  const tempMatrix = useMemo(() => new THREE.Matrix4(), []);

  // 캐시된 행렬
  const cachedInverseViewProj = useRef(new THREE.Matrix4());
  const cachedInverseOblique = useRef(new THREE.Matrix4());

  // 렌더링 직전에 카메라 행렬 동기화 (매 프레임 업데이트)
  useFrame(() => {
    if (!materialRef.current) return;

    materialRef.current.uniforms.uTime.value = clock.getElapsedTime();

    // 카메라 행렬이 유효한지 확인
    if (!camera.projectionMatrix || !camera.matrixWorldInverse) return;

    // 카메라 변경 감지 (projection + view + panOffset)
    const currentPanOffset = getPanOffset();

    // ViewProjection 행렬 생성 (매 프레임 업데이트 - 줌 동기화를 위해)
    tempMatrix
      .copy(camera.projectionMatrix)
      .multiply(camera.matrixWorldInverse);

    // 행렬이 유효한지 확인 (determinant가 0이 아닌지)
    const det = tempMatrix.determinant();
    if (Math.abs(det) < 1e-10) return;

    // InverseViewProjection 계산 (항상 업데이트)
    cachedInverseViewProj.current.copy(tempMatrix).invert();

    // Oblique 역행렬 계산 (캐싱된 calculateObliqueMatrix 사용)
    const obliqueMatrix = calculateObliqueMatrix(
      projectionParams,
      currentPanOffset
    );
    cachedInverseOblique.current.copy(obliqueMatrix).invert();

    // Uniform 업데이트
    materialRef.current.uniforms.uInverseViewProj.value.copy(
      cachedInverseViewProj.current
    );
    materialRef.current.uniforms.uInverseOblique.value.copy(
      cachedInverseOblique.current
    );
  });

  return (
    <mesh frustumCulled={false} renderOrder={-100}>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        ref={materialRef}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        depthWrite={false}
        depthTest={false}
      />
    </mesh>
  );
}
